import { useEffect, useState } from 'react'

import { Box, Card, Chip, Stack, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR'

import { acGetCompanyCustomers } from '../../../store/actions'

import CompanyCustomersFilters from './ScoreRangePopover'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, MAX_POSSIBLE_SCORE, MIN_POSSIBLE_SCORE } from './constants'
import { formatCNPJ } from '../../../utils'


const CompanyCustomersTable = () => {

    const {
        companyCustomers,
        rowCount,
        pagination,
        sorting,
        filter,
        isLoadingCompanyCustomers,
        //hasErrorCompanyCustomers
    } = useSelector(store => store.companyCustomer)

    const dispatch = useDispatch()

    const [tableFilter, setTableFilter] = useState([])
    const [tableSorting, setTableSorting] = useState([])
    const [tablePagination, setTablePagination] = useState({
        pageSize: DEFAULT_PAGE_SIZE,
        pageIndex: DEFAULT_PAGE_INDEX
    })

    const [isFirstQuery, setIsFirstQuery] = useState(true)

    const [[minScore, maxScore], setScoreRange] = useState([MIN_POSSIBLE_SCORE, MAX_POSSIBLE_SCORE])

    useEffect(() => {

        const hasPaginationChanged = (
            tablePagination.pageSize !== DEFAULT_PAGE_SIZE ||
            tablePagination.pageIndex !== DEFAULT_PAGE_INDEX
        )
        const hasFilterChanged = tableFilter.length > 0
        const hasSortingChanged = tableSorting.length > 0
        const hasScoreRangeChanged = minScore !== MIN_POSSIBLE_SCORE || maxScore !== MAX_POSSIBLE_SCORE

        const hasQueryParamsChanged = hasPaginationChanged || hasFilterChanged || hasSortingChanged || hasScoreRangeChanged

        if (hasQueryParamsChanged && isFirstQuery) {
            setIsFirstQuery(false)
            return
        }

        if (isFirstQuery) {
            return
        }

        dispatch(acGetCompanyCustomers({
            filter: [
                ...tableFilter,
                { id: "minScore", value: minScore },
                { id: "maxScore", value: maxScore }
            ],
            pagination: tablePagination,
            sorting: tableSorting
        }))

    }, [tablePagination, tableFilter, tableSorting, isFirstQuery, minScore, maxScore])

    const BypassChip = () => (
        <Chip
            label='bypass'
            color='primary'
            variant='outlined'
            size='small'
        />
    )

    const columns = [
        {
            accessorKey: 'document',
            header: 'CNPJ',
            enableSorting: false,
            enableColumnActions: false,
            accessorFn: (row) => (
                row.edgeCaseResolutionRecord?.edgeCase?.code === 'BYPASS'
                    ? <>{formatCNPJ(row.document)}  <BypassChip /></>
                    : formatCNPJ(row.document)
            )
        },
        {
            accessorKey: 'score',
            header: 'Score',
            enableSorting: true,
            enableColumnActions: false
        },

        //{
        //    accessorKey: 'applied_credit_policy',
        //    header: 'Política Aplicada',
        //    enableColumnActions: false,
        //    enableSorting: false
        //},

        {
            accessorKey: 'consumed_credit_limit_percentage',
            header: 'Porcentagem de Limite Consumido',
            enableColumnActions: false,
            enableSorting: true,
            accessorFn: (row) => `${row.consumed_credit_limit_percentage}%`
        },

        //{
        //    accessorKey: 'old_credit_limit',
        //    header: 'Limite Antigo',
        //    enableSorting: false
        //},
        //{
        //    accessorKey: 'new_credit_limit',
        //    header: 'Limite Novo',
        //    enableSorting: false
        //},

        {
            accessorKey: 'credit_limit_factor',
            header: 'Multiplicador',
            enableColumnActions: false,
            enableSorting: true
        },

        {
            accessorKey: 'seller_code',
            header: 'Código do Vendedor',
            enableSorting: false,
            enableColumnActions: false
        },
        {
            accessorKey: 'updatedAt',
            header: 'Última Consulta',
            enableColumnActions: false,
            enableSorting: true,
            accessorFn: (row) => (
                new Date(row.updatedAt).toLocaleString('pt-BR')
            )
        }
    ]

    const table = useMaterialReactTable({

        columns,
        data: companyCustomers || [],
        localization: MRT_Localization_PT_BR,

        // Actions Column
        //enableRowActions: true,
        //positionActionsColumn: 'last',
        //renderRowActions: renderRowActions,

        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,

        onColumnFiltersChange: setTableFilter,
        onPaginationChange: setTablePagination,
        onSortingChange: setTableSorting,

        enablePagination: true,
        enableColumnActions: true,

        enableHiding: false,
        enableColumnFilters: false,
        enableGlobalFilter: false,
        enableKeyboardShortcuts: false,
        enableTopToolbar: false,

        initialState: { density: 'compact' },

        rowCount,

        state: {
            sorting,
            pagination,
            columnFilters: filter,
            isLoading: isLoadingCompanyCustomers && !companyCustomers?.length,
            isSaving: isLoadingCompanyCustomers && companyCustomers?.length > 0
        },

        // Mui Style Props
        muiTablePaperProps: ({ table }) => ({
            elevation: 0,
            style: {
                zIndex: table.getState().isFullScreen ? 1300 : undefined,
            }
        }),
        muiPaginationProps: {
            rowsPerPageOptions: [5, 10, 15, 20, 25, 30]
        },
        mrtTheme: {
            baseBackgroundColor: "#FFFFFF"
        },

        renderEmptyRowsFallback: () => (
            <Box p={1} justifyItems="center">
                <Typography variant='body2'>Nenhum score calculado até o momento</Typography>
            </Box>
        )

    })

    const setFilter = ({ document, seller_code, minScore, maxScore }) => {

        setScoreRange([minScore, maxScore])

        setTableFilter(curr => {
            const finalFilter = []

            for (const column of columns) {

                const id = column.accessorKey
                const item = curr.find(item => item.id === id)
                const value = (
                    id === 'document' ? document :
                        id === 'seller_code' ? seller_code :
                            undefined
                )

                switch (id) {
                    case 'document':
                    case 'seller_code': {
                        if (value === null) {
                            break
                        }

                        if (!value && !item) {
                            break
                        }

                        if (value) {
                            finalFilter.push({ id, value })
                            break
                        }

                        finalFilter.push(item)
                        break
                    }

                    default:
                        if (item) {
                            finalFilter.push(item)
                        }
                        break
                }
            }

            return finalFilter
        })
    }

    return (
        <Stack gap={3} p={3} component={Card} elevation={0}>

            <Stack>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Tabela de Clientes
                </Typography>
                <Typography variant="body2">
                    Filtre por vendedor, score e visualize a política aplicada para cada cliente
                </Typography>
            </Stack>

            <CompanyCustomersFilters setFilter={setFilter} />

            <MaterialReactTable table={table} />
        </Stack>
    )
}

CompanyCustomersTable.propTypes = {}

export default CompanyCustomersTable

